import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import _debounce from 'lodash/debounce';

import { GoogleMaps } from '@components/_shared/googleMaps';
import {
  buildListingMarker,
  prepareListingMarkerInfo,
} from '@components/_shared/googleMaps/mapUtils';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { FeaturedListingType } from '@utils/types/featuredListing';
import { MapBounds, MarkerType } from '@utils/types/maps';
import { DatesRange } from '@utils/types/calendar';
import { useSearchListings } from '@utils/context/searchListings';
import { getDefaultMapBounds } from '@utils/helpers';
import { useGetListingLink } from '@utils/hooks/useGetListingLink';

import styles from './styles.module.scss';

type Props = {
  listings: FeaturedListingType[];
  selectedListing?: FeaturedListingType;
  zoom: number;
  dates?: DatesRange;
  onMarkerClick?: (id: string) => void;
  resetListingInfo?: () => void;
} & BaseComponentProps;

ListingsMap.defaultProps = {
  zoom: 4,
};

export function ListingsMap(props: Props) {
  const {
    listings,
    zoom,
    selectedListing,
    containerStyle,
    dates,
    onMarkerClick,
    resetListingInfo,
  } = props;
  const [mapMeta, setMapMeta] = useState<{
    bounds: MapBounds;
    markers: MarkerType[];
  }>();
  const [markerListingInfo, setMarkerListingInfo] =
    useState<FeaturedListingType>();
  const [retryBeforeMount, setRetryBeforeMount] = useState(false);
  const { filterParams, changeMapBounds } = useSearchListings();
  const { prepareListingHref } = useGetListingLink();

  useEffect(() => {
    setMarkerListingInfo(selectedListing);
  }, [selectedListing]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (retryBeforeMount) setRetryBeforeMount(false);
    if (typeof window === 'undefined' || !window.google) {
      timer = setTimeout(() => setRetryBeforeMount(true), 50);
      return;
    }

    let bounds = new window.google.maps.LatLngBounds();

    if (!!markerListingInfo) {
      // @ts-ignore
      bounds.extend({
        lng: markerListingInfo.address.coords.lon,
        lat: markerListingInfo.address.coords.lat,
      });
    }

    const listingsWithCoordinates = listings.filter((v) => !!v.address.coords);

    const newMarkers: MarkerType[] = listingsWithCoordinates.map((l) => {
      if (!markerListingInfo) {
        // @ts-ignore
        bounds.extend({
          lng: l.address.coords.lon,
          lat: l.address.coords.lat,
        });
      }
      return prepareListingMarkerInfo(
        l,
        prepareListingHref({
          listingSlug: l.url_slug,
          preserveQuery: true,
        }),
        dates,
        markerListingInfo
      );
    });

    if (!listingsWithCoordinates.length) {
      bounds = getDefaultMapBounds();
    }

    setMapMeta({
      bounds,
      markers: newMarkers,
    });

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [listings, dates, markerListingInfo, retryBeforeMount]);

  const onBoundsChange = (bounds: string) => {
    changeMapBounds(bounds);
  };

  const callbackOnBoundsChange = useMemo(
    () => _debounce(onBoundsChange, 700),
    //eslint-disable-next-line
    []
  );

  return (
    <div className={clsx([styles.container, containerStyle])}>
      <GoogleMaps
        zoom={zoom}
        bounds={mapMeta?.bounds}
        markers={mapMeta?.markers || []}
        onMarkerClick={onMarkerClick}
        callbackOnBoundsChange={callbackOnBoundsChange}
        onInfoWindowClose={resetListingInfo}
        market={filterParams.market}
        buildMarker={buildListingMarker}
      />
    </div>
  );
}
